<!--
  (c) Copyright 2024 Calabrio, Inc.
  All Rights Reserved. www.calabrio.com LICENSED MATERIALS
  Property of Calabrio, Inc., Minnesota, USA

  No part of this publication may be reproduced, stored or transmitted,
  in any form or by any means (electronic, mechanical, photocopying,
  recording or otherwise) without prior written permission from Calabrio, Inc.
-->

<template>
  <div class="wy-analytics-transcript-search-expand" @mouseover="handleTranscriptOver($event)" @mouseout="handleTranscriptOut($event)">
    <div class="div-transcript-header">
      <div class="transcripts-viewer-date-header">
        <div class="sentiment-icon-positive" v-if="transcript.sentiment == 'pos'"></div>
        <div class="sentiment-icon-neutral" v-else-if="transcript.sentiment == 'neu'"></div>
        <div class="sentiment-icon-negative" v-else-if="transcript.sentiment == 'neg'"></div>
        {{ formatMessageHeaderDate(transcript.date) }}
      </div>
      <div class="transcripts-viewer-time-header">{{ getTimeRange(transcript) }}</div>
      <div class="flex-container">
        <el-tooltip v-if="transcript" class="item" effect="dark" placement="bottom">
          <div v-if="transcript.sentiment == 'pos'" slot="content">Positive sentiment</div>
          <div v-else-if="transcript.sentiment == 'neg'" slot="content">Negative sentiment</div>
          <div v-else slot="content">Neutral sentiment</div>
        </el-tooltip>

        <el-tooltip v-if="transcript.isSavedTranscript" ref="unSaveTooltip" class="save-item" content="unsave transcript" effect="dark" placement="bottom">
          <div><el-button v-if="transcript.isSavedTranscript" slot="reference" class="saved-transcript-golden-star" @click="handleRemoveSavedTranscript($event, transcript)"></el-button></div>
        </el-tooltip>
        <el-tooltip v-if="!transcript.isSavedTranscript" ref="saveTooltip" class="save-item" content="save transcript" effect="dark" placement="bottom">
          <div><el-button v-if="!transcript.isSavedTranscript" slot="reference" class="saved-transcript-grey-star" @click="handleSaveTranscript($event, transcript)"></el-button></div>
        </el-tooltip>

        <!-- summary -->
        <!-- Show summary -->
        <el-tooltip v-show="isTranscriptSummaryEnabled && transcript.conversationId && !transcriptSummaryOpened" ref="summaryTooltip" :content="'View summary'" effect="dark" placement="bottom">
          <el-button class="summary-icon" @click="handleShowSummary(transcript)"></el-button>
        </el-tooltip>
        <el-tooltip v-show="isTranscriptSummaryEnabled && transcriptSummaryOpened" ref="summaryTooltip" :content="'Hide summary'" effect="dark" placement="bottom">
          <el-button class="summary-icon" @click="handleHideSummary(transcript)"></el-button>
        </el-tooltip>

        <wy-panel-header-transcript-info :transcript="transcript" :iconType="iconType" :isTranscriptSummaryEnabled="isTranscriptSummaryEnabled"/>

        <el-tooltip v-if="transcript" class="collapse-item" effect="dark" placement="bottom">
          <div slot="content">Collapse</div>
          <div :class="'transcript-minimize-icon'" @click="handleClose"></div>
        </el-tooltip>
      </div>
    </div>

    <div class="div-transcript-content">
      <div>
        <div>
          <div class="transcriptSummary" v-if="transcriptSummaryOpened && !transcriptSummaryNetworkError" v-loading="transcriptSummaryLoading">
            <div class="titleWrapper">
              <div class="summaryTitle"></div>
              Transcript Summary
            </div>
            <div v-if="transcript.contentSummary" class="summary" v-html="contentSummary"></div>
            <div v-if="transcript.contentSummary" class="footer">[ Generated by AI ]</div>
          </div>
          <div v-else-if="transcriptSummaryNetworkError" class="transcriptSummaryError">
            <div class="content">
              <div class="info">
                <el-button class="errorIcon" @click="handleHideSummary"></el-button>
                <div class="body">
                  <div>Summary failed to load.</div>
                  <el-button class="tryAgainBtn" @click="handleShowSummary(transcript)">Try again</el-button>
                </div>
              </div>
              <el-button class="closeIcon" @click="handleHideSummary(transcript)"></el-button>
            </div>
          </div>
        </div>
        <div
          v-for="(message, index) in transcript.messages"
          :key="index"
          :class="{
            'div-transcript-row-cus': message.sentBy == 'Customer',
            'div-transcript-row-bot': message.sentBy == 'Bot',
            'div-transcript-row-agent': message.sentBy == 'Agent',
            'transcript-flex-col': (message.sentBy == 'Agent' && returnTRef(transcript.messages)[1] == index) || (message.sentBy == 'Bot' && returnTRef(transcript.messages)[0] == index),
          }"
          class="div-transcript-row"
        >
          <div v-if="message.sentBy == 'Bot' && returnTRef(transcript.messages)[0] == index" class="bot-connected-row">
            <span class="bot-connected-text">Virtual Agent Connected</span>
          </div>
          <div v-if="message.sentBy == 'Agent' && returnTRef(transcript.messages)[1] == index" class="agent-connected-row"><span class="agent-connected-text">Live Agent Connected</span></div>
          <div style="display: flex; flex-direction: column;">
            <div class="div-sent-by">
              {{ toTranscriptLabel(message.sentBy) }}, <span class="time-stamp-style">{{ handleTimeFormat(message.datetime) }}</span>
            </div>
            &nbsp;
            <div v-if="message && message.text.includes('||')" v-html="message.text.replace(/\|\|+/g, ' | ')" class="div-transcript-text-double-line"></div>
            <div v-else-if="message && message.text.includes('|')" v-html="message.text" class="div-transcript-text-double-line"></div>
            <div v-else v-html="message.text" class="div-transcript-text"></div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import Vue from 'vue';
import WyUtils from '../../../library/wy-utils.js';
import WyPanelHeaderTranscriptInfo from '../../panel-views/wy-panel-header-transcript-info.vue';
import { mapActions, mapGetters } from 'vuex';
import moment from 'moment';

export default Vue.extend({
  name: 'wy-analytics-transcript-search-expand',
  props: ['parent', 'transcript', 'from', 'transcriptSummaryOpened', 'transcriptSummaryNetworkError', 'iconType'],
  components: {
    'wy-panel-header-transcript-info': WyPanelHeaderTranscriptInfo,
  },
  data() {
    return {
      isOverTranscriptContent: false,
      transcriptSummaryLoading: false,
    };
  },
  computed: {
    ...mapGetters(['isC1Auth', 'companyConfig', 'isRoleAllow']),
    isTranscriptSummaryEnabled() {
      if(this.isC1Auth) {
        return this.isRoleAllow(900007);
      }
      else {
        return (this.companyConfig ? this.companyConfig.enableTranscriptSummary : false);
      }
    },

    contentSummary() {
      return this.transcript.contentSummary;
    },
    pageName() {
      return this.from === 'Conversation Topics' ? 'Conversation Topics' : 'Transcript Search';
    },
    viewName() {
      return this.from === 'Conversation Topics' ? 'Conversation Topics Individual - Transcripts' : 'Transcript Search';
    },
  },

  methods: {
    ...mapActions(['fetchTranscriptSummary']),
    
    async handleShowSummary(transcript) {
      this.$emit('handle-transcript-summary', {
        conversationId: transcript.conversationId,
        opened: true,
      });
      this.transcriptSummaryLoading = true;

      const sessionData = sessionStorage.getItem(transcript.conversationId);
      if (sessionData || transcript.contentSummary) {
        const summary = sessionData ? JSON.parse(sessionData).summary : transcript.contentSummary;
        this.transcriptSummaryLoading = false;
        transcript.contentSummary =
          '<ul>' +
          summary
            .replace(/-/g, '')
            .split('\n')
            .map(function(item) {
              return '<li>' + item.trim() + '</li>';
            })
            .join('') +
          '</ul>';

        this.$emit('handle-transcript-summary', {
          conversationId: transcript.conversationId,
          opened: true,
          contentSummary: transcript.contentSummary,
        });

        return;
      }

      const contentSummary = await this.fetchTranscriptSummary({
        conversationId: transcript.conversationId,
      });
      this.transcriptSummaryLoading = false;

      if (contentSummary.status == 200 && (contentSummary.data && contentSummary.data.summary)) {
        transcript.contentSummary =
          '<ul>' +
          contentSummary.data.summary
            .replace(/-/g, '')
            .split('\n')
            .map(function(item) {
              return '<li>' + item.trim() + '</li>';
            })
            .join('') +
          '</ul>';

        this.$emit('handle-transcript-summary', {
          conversationId: transcript.conversationId,
          opened: true,
          contentSummary: transcript.contentSummary,
        });

        sessionStorage.setItem(transcript.conversationId, JSON.stringify(contentSummary.data));
        return;
      }

      this.$emit('handle-transcript-summary', {
        conversationId: transcript.conversationId,
        opened: true,
        isError: true,
      });
    },

    handleHideSummary(transcript) {
      this.$emit('handle-transcript-summary', {
        conversationId: transcript.conversationId,
        opened: false,
        isError: false,
      });
    },

    handleTranscriptOver() {
      this.isOverTranscriptContent = true;
    },
    handleTranscriptOut() {
      this.isOverTranscriptContent = false;
    },
    handleTimeFormat(datetime) {
      return moment(datetime).format('h:mm a');
    },
    returnTRef(messages) {
      let sentByArr = [];
      for (let i = 0; i < messages.length; i++) {
        let entries = Object.entries(messages[i]);
        entries.map(([key, val]) => {
          if (key == 'sentBy') {
            sentByArr.push(val);
          }
        });
      }
      return [sentByArr.indexOf('Bot'), sentByArr.indexOf('Agent')];
    },
    handleSaveTranscript(event, transcript) {
      this.parent.addSavedTranscript(event, transcript);
      if (this.$refs.saveTooltip && this.$refs.unSaveTooltip) {
        this.$refs.saveTooltip.$el.style.display = 'none';
        this.$refs.unSaveTooltip.$el.style.display = 'block';
      }
    },
    handleRemoveSavedTranscript(event, transcript) {
      this.parent.removeSavedTranscript(event, transcript);
      if (this.$refs.saveTooltip && this.$refs.unSaveTooltip) {
        this.$refs.unSaveTooltip.$el.style.display = 'none';
        this.$refs.saveTooltip.$el.style.display = 'block';
      }
    },
    formatMessageHeaderDate(date) {
      return WyUtils.formatDateWithDay(date);
    },

    getTimeRange(transcript) {
      return WyUtils.getTimeRange(transcript);
    },

    toTranscriptLabel(val) {
      return WyUtils.toTranscriptLabel(val);
    },

    handleTimeInNiceFormat(Millis) {
      let seconds = Millis / 1000;

      return WyUtils.toNiceTimeFormat(seconds);
    },
    handleClose() {
      this.$parent.close();
    },
  },
  mounted() {
    this.$root.$on(`fire-global-filter-change-events`, (globalFiloterHasChanged) => {
      if (globalFiloterHasChanged) {
        this.handleClose();
      }
    });
    document.querySelector('.nav_top_bar').addEventListener('click', () => {
      this.handleClose();
    });
  },
  beforeDestroy() {
    if (document.querySelector('.nav_top_bar')) {
      document.querySelector('.nav_top_bar').removeEventListener('click', () => {
        this.handleClose();
      });
    }
  },
});
</script>

<style lang="scss">
.wy-analytics-transcript-search-expand {
  border: 1px solid #e8e8e8;
  border-radius: 4px;
  z-index: 99999;
  position: relative;
  margin-top: 12px;
  height: 97%;
  background: white;

  ul,
  li {
    list-style-type: disc !important;
    list-style-position: inside !important;
    padding: 4px 0px 0px 0px !important;
    margin-top: 4px !important;
    margin-left: 12px !important;
    text-indent: -1.5em !important;
    font-family: 'Helvetica Neue', Helvetica, Arial, sans-serif !important;
    font-size: 16px !important;
  }

  .div-transcript-header {
    height: 36px;
    border-bottom: 1px #e8e8e8 solid;
    display: inline-block;
    // min-width: 257px;
    //@functionmin-width: 281px;
    width: 100%;
    display: flex;
    justify-content: space-between;
    //background-color: #F5F6F7;
    background-color: #ffffff;
    border-top-right-radius: 10px;
    border-top-left-radius: 10px;

    .transcripts-viewer-date-header {
      display: inline-block;
      font-size: 16px;
      padding-left: 10px;
      min-width: 98px;
      padding-top: 12px;
    }

    .transcripts-viewer-time-header {
      float: right;
      font-size: 16px;
      padding-left: 0px;
      padding-top: 11px;
      // width: 100px;
      width: 160px;
    }
    .el-tooltip.save-item {
      position: relative;
      left: 24px;
      top: -10px;
      line-height: 0;
    }
    .el-tooltip.collapse-item {
      position: relative;
      top: -10px;
    }
    .saved-transcript-golden-star {
      background-image: url('../../../assets/icons/icon_golden_star.svg');
      background-size: 100%;
      background-repeat: no-repeat;
      background-position-y: 0px;
      background-position-x: 0px;
      height: 20px;
      width: 20px;
      border: 0px;
      margin: 0px !important;
      padding: 0px !important;
      position: relative;
      border-radius: 0px;
      top: 17px;
    }

    .saved-transcript-grey-star {
      background-image: url('../../../assets/icons/icon_grey_star.svg');
      background-size: 100%;
      background-repeat: no-repeat;
      background-position-y: 0px;
      background-position-x: 0px;
      height: 20px;
      width: 20px;
      border: 0px;
      margin: 0px !important;
      padding: 0px !important;
      position: relative;
      border-radius: 0px;
      top: 17px;
    }

    .transcript-minimize-icon {
      background-image: url('../../../assets/icons/icon_transcript_search_minimize.svg');
      cursor: pointer;
      background-size: 100%;
      background-repeat: no-repeat;
      background-position-y: 0px;
      background-position-x: 0px;
      height: 20px;
      width: 20px;
      border: 0px;
      margin: 0px !important;
      padding: 0px !important;
      position: relative;
      border-radius: 0px;
      top: 7px !important;
      left: 0px;
    }

    .el-button:focus {
      background-color: unset;
    }

    .el-button:hover {
      background-color: unset;
    }

    .flex-container {
      display: flex;
      justify-content: space-around;
      width: 150px;

      .transcripts-viewer-header-right {
        line-height: 18px;

        .popper-item {
          margin-bottom: 15px;
        }

        overflow-y: auto;
        height: 204px !important;
        padding-right: 20px;

        &::-webkit-scrollbar {
          background-color: #000 !important;
          width: 8px !important;
        }

        &::-webkit-scrollbar-thumb {
          background: #c8c8c8 !important;
          border-radius: 5px;
        }
      }
    }

    .sentiment-icon-positive {
      cursor: default;
      background-image: url('../../../assets/icons/icon_positive_senti.svg');
      background-size: 80%;
      background-repeat: no-repeat;
      background-position-y: 0px;
      background-position-x: 0px;
      position: relative;
      top: 0px;
      left: 0px;
      border-radius: 100px;
      border: 0px;
      margin: 0px 0px 0 0;
      padding: 12px 10px 14px 14px;
      float: left;
    }

    .sentiment-icon-neutral {
      cursor: default;
      background-image: url('../../../assets/icons/icon_neutral_senti.svg');
      background-size: 80%;
      background-repeat: no-repeat;
      background-position-y: 0px;
      background-position-x: 0px;
      position: relative;
      top: 0px;
      left: 0px;
      border-radius: 100px;
      border: 0px;
      margin: 0px 0px 0 0;
      padding: 12px 10px 14px 14px;
      float: left;
    }

    .sentiment-icon-negative {
      cursor: default;
      background-image: url('../../../assets/icons/icon_negative_senti.svg');
      background-size: 80%;
      background-repeat: no-repeat;
      background-position-y: 0px;
      background-position-x: 0px;
      position: relative;
      top: 0px;
      left: 0px;
      border-radius: 100px;
      border: 0px;
      margin: 0px 0px 0 0;
      padding: 12px 10px 14px 14px;
      float: left;
    }

    .summary-icon {
      background-image: url('../../../assets/icons/summary_icon.svg');
      background-size: 100%;
      background-repeat: no-repeat;
      background-position-y: 0px;
      background-position-x: 0px;
      position: relative;
      height: 20px;
      width: 20px;
      border: 0px;
      position: relative;
      top: 7px;
      left: 16px;
      margin: 0px !important;
      padding: 0px !important;
      border-radius: 0px;
    }
  }

  .div-transcript-content {
    height: calc(100% - 49px);
    width: 100%;
    padding: 5px 0px;
    overflow-y: auto;
    overflow-x: hidden;
    font-size: 16px;

    &::-webkit-scrollbar {
      width: 15px;
    }

    &::-webkit-scrollbar-thumb {
      border-radius: 50px;
      background-color: #cad2da;
    }

    &::-webkit-scrollbar-track {
      background: #ffffff;
    }

    &::-webkit-scrollbar:vertical {
      width: 15px;
    }

    &::-webkit-scrollbar-thumb:vertical {
      border-radius: 160px;
      border: 4px solid transparent;
      background-clip: content-box;
      background-color: #cad2da;
    }

    &::-webkit-scrollbar-track:vertical {
      border-top: none !important;
      border-left: 1px solid #eee;
    }

    .bot-connected-row {
      padding: 10px 15px 8px 11px;
      border-bottom: 10px #fff solid;
      cursor: default;
      background-image: url('../../../assets/icons/icon_transcript_bot.svg');
      background-repeat: no-repeat;
      position: relative;
      margin-bottom: 10px;
      width: 99.5%;
      right: 12px;
      background-position-y: 10px;
      background-position-x: 43%;
    }

    .agent-connected-row {
      padding: 10px 15px 8px 11px;
      border-bottom: 10px #fff solid;
      cursor: default;
      background-image: url('../../../assets/icons/icon_transcript_agent.svg');
      background-repeat: no-repeat;
      margin-bottom: 10px;
      position: relative;
      right: 12px;
      background-position-y: 12px;
      width: 99.5%;
      background-position-x: 43%;
    }

    .div-transcript-row {
      display: inline-flex;
      padding: 10px 15px 8px 11px;
      min-width: 245px;
      background-color: #effcff;
      margin: 5px 10px;
      width: 97.3% !important;
      cursor: default;

      .div-sent-by {
        font-weight: 500;
        font-size: 16px;
        padding-right: 10px;
        min-width: 33px;
        line-height: 14px;
        color: #000;

        span.time-stamp-style {
          color: #4b5158;
          font-size: 16px;
          font-weight: 400;
          font-weight: 400;
        }
      }

      .div-transcript-text-double-line {
        word-break: break-word;
        // min-width: 178px;
        padding-left: 23px;
        padding-right: 11px;
        color: #106fbc;
        background-image: url('../../../assets/icons/icon_transcript_doubleline.svg');
        background-repeat: no-repeat;
        background-size: 14px;
        background-position-y: -1px;
        background-position-x: 0%;

        .highlight-keywords {
          background-color: rgba(255, 177, 0, 0.22);
        }
      }

      .div-transcript-text {
        word-break: break-word;
        // min-width: 178px;
        font-size: 16px;
        color: var(--site-font-secondary-color);
        padding-right: 11px;

        .highlight-keywords {
          background-color: rgba(255, 177, 0, 0.22);
        }
      }
    }

    .div-transcript-row-bot {
      background-color: #effcff;
      width: 99.5%;
    }

    .div-transcript-row.div-transcript-row-cus {
      background-color: #f8f6f6 !important;
    }

    .div-transcript-row.div-transcript-row-bot.transcript-flex-col,
    .div-transcript-row.div-transcript-row-agent.transcript-flex-col {
      padding: 0px 0px 10px 11px;
      display: flex;
      width: 98.2% !important;
      flex-direction: column;
      align-items: flex-start;
    }

    span.agent-connected-text {
      margin-left: 45%;
      margin-bottom: 15%;
      font-size: 16px;
      font-weight: 700;
    }

    span.bot-connected-text {
      margin-left: 45%;
      margin-bottom: 15%;
      font-size: 16px;
      font-weight: 700;
    }

    .div-transcript-row-agent {
      background-color: #eeeefa;
    }
  }

  .transcriptSummary {
    display: flex;
    flex-direction: column;
    justify-content: space-between;
    align-items: center;
    font-size: 14px;
    margin: 6px 0px;
    padding: 24px;
    border-bottom: 1px solid #e5e5e5;
    min-height: 150px;

    .titleWrapper {
      font-size: 14px;
      font-family: 'Helvetica Neue', Helvetica, Arial, sans-serif;
      margin-left: -70px;
      font-size: 16px;
      font-weight: 700;

      .summaryTitle {
        background-image: url('../../../assets/icons/summary_title_icon.svg');
        background-size: 80%;
        background-repeat: no-repeat;
        background-position-y: 3px;
        background-position-x: 5px;
        position: relative;
        left: 7px;
        border-radius: 100px;
        border: 0px;
        top: 0px;
        margin: -3px 25px 0 0;
        padding: 12px 10px 18px 14px;
        float: left;
        color: var(--site-font-tertiary-color);
      }
    }

    .summary {
      color: rgb(75, 75, 75);
      display: flex;
      justify-content: flex-start;
      width: 100%;
    }

    .footer {
      margin-top: 14px;
      display: flex;
      justify-content: flex-end;
      width: 100%;
      color: #8c8c8c;
      font-size: 12px;
    }
  }

  .transcriptSummaryError {
    display: flex;
    justify-content: center;
    align-items: center;
    font-size: 14px;
    margin: 6px 10px;
    padding: 6px 40px 30px 40px;
    border-bottom: 1px solid #e5e5e5;
    min-height: 150px;

    .content {
      display: flex;
      width: 408px;
      justify-content: space-between;
      padding: 20px;
      border: 1px solid #ebafba;

      .info {
        display: flex;
        gap: 24px;
        font-weight: 500;
        font-size: 14.22px;

        .body {
          display: flex;
          flex-direction: column;
          font-weight: 600;
          gap: 16px;
        }

        .tryAgainBtn {
          border-radius: 4px;
          max-width: 100px;
          max-height: 35px;
          font-size: 16px;
          color: #202223;
          font-weight: 400;
          border: 1px solid #ebafba;
          display: flex;
          justify-content: center;
          align-items: center !important;
          padding: 12px 10px 12px 10px;
          margin-left: 0px;
          margin-right: 0px;
        }
      }

      .errorIcon {
        background-image: url('../../../assets/icons/icon_error_square.svg');
        height: 12px;
        width: 12px;
        background-repeat: no-repeat;
        background-position-y: 0px;
        background-position-x: 3px;
        margin-bottom: 8px;
        padding: 12px;
        border: none;
        cursor: default;

        &:hover {
          background-color: unset;
        }
      }

      .closeIcon {
        background-image: url('../../../assets/icons/icon_customer_close.svg');
        height: 12px;
        width: 12px;
        background-repeat: no-repeat;
        background-position-y: 6px;
        background-position-x: 6px;
        margin-bottom: 8px;
        padding: 12px;
        border: none;
        cursor: pointer;
        margin-right: 0px;
        &:hover {
          background-color: transparent;
        }
      }
    }
  }
}

.contact-reason-transcript-tab-info-pop-over {
  background: black;
  color: #e4e8ec;
  font-size: 14px;
  padding: 12px 15px 10px 15px;
  border: black !important;
  font-family: 'Helvetica Neue', Helvetica, Arial, sans-serif;

  .popper__arrow {
    border-top-color: var(--site-font-chart-color) !important;
    &:after {
      border-top-color: var(--site-font-chart-color) !important;
    }
  }
}
</style>
